import type { Tokens } from 'marked'
import { marked } from 'marked'

type LinkRendererOptions = {
  allowLinks: boolean
}

// Shared link renderer
function createLinkRenderer({ allowLinks }: LinkRendererOptions) {
  return ({ href, title, tokens }: Tokens.Link): string => {
    const text = tokens.map(token => token.raw).join('')

    if (!allowLinks) {
      return text
    }

    const titleAttr = title ? ` title="${title}"` : ''
    return `<a href="${href}"${titleAttr} target="_blank" rel="nofollow noindex" data-ag-md-link>${text}</a>`
  }
}

const renderer = new marked.Renderer()
renderer.link = createLinkRenderer({ allowLinks: true })

marked.use({
  renderer,
  breaks: true,
  gfm: true
})

// For text inside a linked element, where a nested link would be invalid
const linklessRenderer = new marked.Renderer()
linklessRenderer.link = createLinkRenderer({ allowLinks: false })

export function renderMarkdownBlock(content: string, { allowLinks }: LinkRendererOptions = { allowLinks: true }): string {
  return marked.parse(content, allowLinks ? undefined : { renderer: linklessRenderer }) as string
}

// Inline renderer for inline-only markdown
const inlineRenderer = new marked.Renderer()
inlineRenderer.link = createLinkRenderer({ allowLinks: true })

export function renderMarkdownInline(content: string, { allowLinks }: LinkRendererOptions = { allowLinks: true }): string {
  return marked.parseInline(content, {
    renderer: allowLinks ? inlineRenderer : linklessRenderer,
    breaks: true,
    gfm: true
  }) as string
}

export function renderMarkdownInAnchor(content: string): string {
  return renderMarkdownInline(content, { allowLinks: false })
}
